import { __rest } from "tslib";
import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { Controller, FormProvider, useForm, useFormContext, } from "react-hook-form";
import { cn } from "../../lib/utils";
import { Label } from "../../components/ui/label";
/**
 * @example
 *
 * ```jsx
 * import {
 *   toast,
 *   Button,
 *   useForm,
 *   Form,
 *   FormControl,
 *   FormDescription,
 *   FormField,
 *   FormItem,
 *   FormLabel,
 *   FormMessage,
 *   Input,
 * } from "../../components/ui/form"
 *
 * function Demo() {
 *   const form = useForm({
 *     defaultValues: {
 *       username: "",
 *     },
 *   })
 *
 *   function onSubmit(data) {
 *     toast({
 *       title: "You submitted the following values:",
 *       description: (
 *         <pre className="mt-2 w-[340px] rounded-md bg-slate-950 p-4">
 *           <code className="text-white">{JSON.stringify(data, null, 2)}</code>
 *         </pre>
 *       ),
 *     })
 *   }
 *
 *   return (
 *     <Form {...form}>
 *       <form onSubmit={form.handleSubmit(onSubmit)} className="w-2/3 space-y-6">
 *         <FormField
 *           control={form.control}
 *           name="username"
 *           render={({ field }) => (
 *             <FormItem>
 *               <FormLabel>Username</FormLabel>
 *               <FormControl>
 *                 <Input placeholder="shadcn" {...field} />
 *               </FormControl>
 *               <FormDescription>
 *                 This is your public display name.
 *               </FormDescription>
 *               <FormMessage />
 *             </FormItem>
 *           )}
 *         />
 *         <Button type="submit">Submit</Button>
 *       </form>
 *     </Form>
 *   )
 * }
 * ```
 */
const Form = FormProvider;
const FormFieldContext = React.createContext({});
const FormField = (_a) => {
    var props = __rest(_a, []);
    return (React.createElement(FormFieldContext.Provider, { value: { name: props.name } },
        React.createElement(Controller, Object.assign({}, props))));
};
const useFormField = () => {
    const fieldContext = React.useContext(FormFieldContext);
    const itemContext = React.useContext(FormItemContext);
    const { getFieldState, formState } = useFormContext();
    const fieldState = getFieldState(fieldContext.name, formState);
    if (!fieldContext) {
        throw new Error("useFormField should be used within <FormField>");
    }
    const { id } = itemContext;
    return Object.assign({ id, name: fieldContext.name, formItemId: `${id}-form-item`, formDescriptionId: `${id}-form-item-description`, formMessageId: `${id}-form-item-message` }, fieldState);
};
const FormItemContext = React.createContext({});
const FormItem = React.forwardRef((_a, ref) => {
    var { className } = _a, props = __rest(_a, ["className"]);
    const id = React.useId();
    return (React.createElement(FormItemContext.Provider, { value: { id } },
        React.createElement("div", Object.assign({ ref: ref, className: cn("space-y-2", className) }, props))));
});
FormItem.displayName = "FormItem";
const FormLabel = React.forwardRef((_a, ref) => {
    var { className } = _a, props = __rest(_a, ["className"]);
    const { error, formItemId } = useFormField();
    return (React.createElement(Label, Object.assign({ ref: ref, className: cn(error && "text-destructive", className), htmlFor: formItemId }, props)));
});
FormLabel.displayName = "FormLabel";
const FormControl = React.forwardRef((_a, ref) => {
    var props = __rest(_a, []);
    const { error, formItemId, formDescriptionId, formMessageId } = useFormField();
    return (React.createElement(Slot, Object.assign({ ref: ref, id: formItemId, "aria-describedby": !error
            ? `${formDescriptionId}`
            : `${formDescriptionId} ${formMessageId}`, "aria-invalid": !!error }, props)));
});
FormControl.displayName = "FormControl";
const FormDescription = React.forwardRef((_a, ref) => {
    var { className } = _a, props = __rest(_a, ["className"]);
    const { formDescriptionId } = useFormField();
    return (React.createElement("p", Object.assign({ ref: ref, id: formDescriptionId, className: cn("text-sm text-muted-foreground", className) }, props)));
});
FormDescription.displayName = "FormDescription";
const FormMessage = React.forwardRef((_a, ref) => {
    var { className, children } = _a, props = __rest(_a, ["className", "children"]);
    const { error, formMessageId } = useFormField();
    const body = error ? String(error === null || error === void 0 ? void 0 : error.message) : children;
    if (!body) {
        return null;
    }
    return (React.createElement("p", Object.assign({ ref: ref, id: formMessageId, className: cn("text-sm font-medium text-destructive", className) }, props), body));
});
FormMessage.displayName = "FormMessage";
export { useForm, useFormField, Form, FormItem, FormLabel, FormControl, FormDescription, FormMessage, FormField, };
//# sourceMappingURL=form.js.map